import React, {useEffect, useLayoutEffect, useRef, useState} from "react";
import styles from "./FeedbackTable.module.css";
import {TextInMarkdown} from "../../../../elements/textInMarkdown/TextInMarkdown";

// Two lines of 21px: the height the answer is clamped to
const COLLAPSED_HEIGHT = 42;
// Matches the max-height transition in FeedbackTable.module.css
const TRANSITION_MS = 200;

interface RatedAnswerProps {
    text: string
}

export const RatedAnswer: React.FC<RatedAnswerProps> = ({text}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    // The ellipsis only comes back once the collapse animation has finished
    const [isClampApplied, setIsClampApplied] = useState(true);
    const [isClamped, setIsClamped] = useState(false);
    const [fullHeight, setFullHeight] = useState(COLLAPSED_HEIGHT);
    const textRef = useRef<HTMLDivElement>(null);
    const clampTimeout = useRef<ReturnType<typeof setTimeout>>();

    // Compare with the fixed two-line height: while the collapse animates the text is not clamped yet,
    // so its own clientHeight would equal its full height and hide the toggle
    useLayoutEffect(() => {
        const element = textRef.current;
        if (element) {
            setIsClamped(element.scrollHeight > COLLAPSED_HEIGHT + 1);
        }
    }, [text]);

    useEffect(() => () => clearTimeout(clampTimeout.current), []);

    const toggle = () => {
        clearTimeout(clampTimeout.current);
        if (isExpanded) {
            setIsExpanded(false);
            clampTimeout.current = setTimeout(() => setIsClampApplied(true), TRANSITION_MS);
        } else {
            setFullHeight(textRef.current?.scrollHeight ?? COLLAPSED_HEIGHT);
            setIsClampApplied(false);
            setIsExpanded(true);
        }
    };

    return <div className={styles.ratedAnswer}>
        <div className={styles.ratedAnswerClip} style={{maxHeight: isExpanded ? fullHeight : COLLAPSED_HEIGHT}}>
            <div ref={textRef} className={isClampApplied ? styles.ratedAnswerText : styles.ratedAnswerFull}>
                <TextInMarkdown text={text}/>
            </div>
        </div>
        {(isClamped || isExpanded) && <button
            type="button"
            className={styles.showMore}
            aria-expanded={isExpanded}
            onClick={toggle}
        >
            {isExpanded ? "Show less" : "Show more"}
        </button>}
    </div>
}
